// Role and Permission Configuration
// This file defines all roles and their default permissions

export interface RoleConfig {
  level: number;
  label: string;
  color: string;
  description: string;
}

export interface FeatureConfig {
  id: string;
  label: string;
  description: string;
  category: string;
  route?: string;
}

export type RoleKey = 'superadmin' | 'admin' | 'auditor' | 'suppliant' | 'board_member' | 'resident' | 'subcontractor' | 'external';

export const ROLES: Record<RoleKey, RoleConfig> = {
  superadmin: { 
    level: 100, 
    label: 'Superadmin', 
    color: 'bg-purple-100 text-purple-800',
    description: 'Fullständig systemåtkomst'
  },
  admin: { 
    level: 80, 
    label: 'Admin', 
    color: 'bg-red-100 text-red-800',
    description: 'Föreningsadministratör'
  },
  auditor: { 
    level: 70, 
    label: 'Revisor', 
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Revisor med granskningsbehörighet'
  },
  suppliant: { 
    level: 70, 
    label: 'Suppliant', 
    color: 'bg-teal-100 text-teal-800',
    description: 'Suppleant i styrelsen'
  },
  board_member: { 
    level: 60, 
    label: 'Styrelsemedlem', 
    color: 'bg-amber-100 text-amber-800',
    description: 'Styrelsemedlem med utökad åtkomst'
  },
  resident: { 
    level: 20, 
    label: 'Boende', 
    color: 'bg-slate-100 text-slate-800',
    description: 'Boende i föreningen'
  },
  subcontractor: { 
    level: 10, 
    label: 'Leverantör', 
    color: 'bg-green-100 text-green-800',
    description: 'Extern leverantör'
  },
  external: { 
    level: 7, 
    label: 'Extern', 
    color: 'bg-sky-100 text-sky-800',
    description: 'Extern användare med begränsad åtkomst'
  }
};

// All available features/permissions
export const FEATURES: Record<string, FeatureConfig> = {
  // Dashboard & Overview
  dashboard: {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Visa översikt och statistik',
    category: 'Översikt',
    route: '/dashboard'
  },
  
  // Issues
  issues_view: {
    id: 'issues_view',
    label: 'Visa ärenden',
    description: 'Se lista över ärenden',
    category: 'Ärenden',
    route: '/issues'
  },
  issues_create: {
    id: 'issues_create',
    label: 'Skapa ärenden',
    description: 'Skapa nya felanmälningar',
    category: 'Ärenden',
    route: '/issues/new'
  },
  issues_manage: {
    id: 'issues_manage',
    label: 'Hantera ärenden',
    description: 'Ändra status, tilldela, kommentera',
    category: 'Ärenden'
  },
  
  // Properties & Apartments
  properties: {
    id: 'properties',
    label: 'Fastigheter',
    description: 'Hantera fastigheter',
    category: 'Fastigheter',
    route: '/properties'
  },
  apartments: {
    id: 'apartments',
    label: 'Lägenheter',
    description: 'Hantera lägenheter och förråd',
    category: 'Fastigheter',
    route: '/apartments'
  },
  
  // Users & Residents
  users_view: {
    id: 'users_view',
    label: 'Visa användare',
    description: 'Se lista över användare/boende',
    category: 'Användare',
    route: '/residents'
  },
  users_manage: {
    id: 'users_manage',
    label: 'Hantera användare',
    description: 'Skapa, redigera, ta bort användare',
    category: 'Användare'
  },
  users_roles: {
    id: 'users_roles',
    label: 'Ändra roller',
    description: 'Ändra användares roller/behörigheter',
    category: 'Användare'
  },
  
  // Orders & Suppliers
  orders: {
    id: 'orders',
    label: 'Arbetsordrar',
    description: 'Hantera arbetsordrar',
    category: 'Leverantörer',
    route: '/orders'
  },
  suppliers: {
    id: 'suppliers',
    label: 'Leverantörer',
    description: 'Hantera leverantörer',
    category: 'Leverantörer',
    route: '/suppliers'
  },
  
  // Communication
  email: {
    id: 'email',
    label: 'E-post/Meddelanden',
    description: 'Skicka och läsa meddelanden',
    category: 'Kommunikation',
    route: '/email'
  },
  board_email: {
    id: 'board_email',
    label: 'Styrelsens e-post',
    description: 'Åtkomst till styrelsens gemensamma e-postkonto (IMAP)',
    category: 'Kommunikation',
    route: '/mail'
  },
  service_messages: {
    id: 'service_messages',
    label: 'Servicemeddelanden',
    description: 'Skicka meddelanden till alla boende',
    category: 'Kommunikation',
    route: '/service-messages'
  },
  
  // Settings
  settings: {
    id: 'settings',
    label: 'Inställningar',
    description: 'Föreningsinställningar',
    category: 'Admin',
    route: '/settings'
  }
};

// Default permissions per role (can be overridden per organization)
export const DEFAULT_PERMISSIONS: Record<RoleKey, string[]> = {
  superadmin: Object.keys(FEATURES), // All permissions
  
  admin: Object.keys(FEATURES), // Admin has all permissions (same as superadmin)
  
  auditor: [
    'dashboard',
    'issues_view',
    'properties', 'apartments',
    'users_view',
    'orders',
    'email'
    // Revisorer har läsbehörighet för granskning
  ],
  
  suppliant: [
    'dashboard',
    'issues_view', 'issues_create', 'issues_manage',
    'properties', 'apartments',
    'users_view',
    'orders',
    'email',
    'board_email',
    'settings'
    // Suppleanter har liknande behörigheter som styrelsemedlemmar
  ],
  
  board_member: [
    'dashboard',
    'issues_view', 'issues_create', 'issues_manage',
    'properties', 'apartments',
    'users_view',
    'orders',
    'email',
    'board_email',
    'settings'
  ],
  
  resident: [
    'dashboard',
    'issues_view', 'issues_create'
  ],
  
  subcontractor: [
    'orders' // Only see assigned orders
  ],
  
  external: [
    'dashboard',
    'issues_view'
  ]
};

// Feature categories for grouping in UI
export const FEATURE_CATEGORIES = [
  'Översikt',
  'Ärenden',
  'Fastigheter',
  'Användare',
  'Leverantörer',
  'Kommunikation',
  'Admin'
] as const;

export type FeatureCategory = typeof FEATURE_CATEGORIES[number];

// Helper to check if a role has a permission
export const hasPermission = (
  role: RoleKey | string, 
  permissionId: string, 
  customPermissions: Record<string, string[]> | null = null
): boolean => {
  // If custom permissions are provided, use them
  if (customPermissions && customPermissions[role]) {
    return customPermissions[role].includes(permissionId);
  }
  // Otherwise use defaults
  return DEFAULT_PERMISSIONS[role as RoleKey]?.includes(permissionId) || false;
};

// Helper to get all features for a category
export const getFeaturesByCategory = (category: string): FeatureConfig[] => {
  return Object.values(FEATURES).filter(f => f.category === category);
};
